import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import MessageMarkdown from './MessageMarkdown';
import styles from './AiAssistant.module.scss';

type Props = {
  // The reasoning text: the live THINKING-frame buffer while streaming, or the
  // persisted message's `thinking` field on reload — same panel for both.
  thinking: string;
  // True while the turn is still streaming, so reasoning may still be arriving
  // and MessageMarkdown defers directive fetches until the message persists.
  isStreaming?: boolean;
};

// The assistant's extended-thinking (reasoning) output, in a disclosure above
// the answer (BE guide §4/§5). Collapsed by default — reasoning is a secondary,
// on-demand read — with a compact header that fits inside the answer bubble.
function ThoughtsPanel({ thinking, isStreaming }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.thoughts}>
      <button
        type="button"
        className={styles.thoughtsToggle}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <MaterialIcon
          icon="psychology"
          symbolsOutlined
          size="md-16"
          className={styles.thoughtsIcon}
        />
        <Typography variant="caption" className={styles.thoughtsLabel}>
          {t('ai-thoughts-label')}
        </Typography>
        <MaterialIcon
          icon="expand_more"
          size="md-16"
          className={classNames(styles.thoughtsChevron, {
            [styles.thoughtsChevronOpen]: open,
          })}
        />
      </button>
      {open && (
        <div className={styles.thoughtsContent}>
          {/* plain: reasoning quotes the `:documentation{id}` chip syntax
              verbatim — render it as literal text, never as a live chip. */}
          <MessageMarkdown content={thinking} isStreaming={isStreaming} plain />
        </div>
      )}
    </div>
  );
}

export default ThoughtsPanel;
